<template>
  <v-flex
    id="doctorsPack"
    class="question"
  >
    <h2 class="display-3 display-3_dark">
      {{ getDoctorsPackQuestions.label }}
    </h2>
    <div v-if="getDoctorsPackQuestions.text">
      <p
        v-for="(text, index) in getDoctorsPackQuestions.text"
        :key="index"
        class="mb-0 f-1"
      >
        {{ text }}
      </p>
    </div>
    <ul class="questions-list">
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('checked_medical_areas')"
        class="mt-4 order-1"
      >
        <p
          class="header mb-0"
          :class="{ required: getDoctorsPackQuestions.checked_medical_areas.required }"
        >
          {{ getDoctorsPackQuestions.checked_medical_areas?.label }}
        </p>
        <v-layout wrap>
          <v-flex
            v-for="(item, index) in medicalAreas"
            :key="index"
            md4
            sm6
            xs12
          >
            <v-checkbox
              v-model="checkedMedicalAreas"
              v-validate="{ required: getDoctorsPackQuestions.checked_medical_areas.required }"
              multiple
              :label="item?.description"
              :value="item.id"
              :error="errors.has('checkedMedicalAreas')"
              :error-messages="
                index + 1 === medicalAreas.length ? errors.collect('checkedMedicalAreas') : null
              "
              :data-vv-as="$t('label.question')"
              :data-vv-name="'checkedMedicalAreas'"
              :disabled="readOnly"
            />
          </v-flex>
        </v-layout>
        <div v-if="showOtherField && getDoctorsPackQuestions.hasOwnProperty('other_medical_areas')">
          <p
            class="fw-600 mt-3"
            :class="{ required: getDoctorsPackQuestions.other_medical_areas.required }"
          >
            {{ getDoctorsPackQuestions.other_medical_areas?.label }}
          </p>
          <v-textarea
            v-model="otherMedicalAreas"
            v-validate="{
              ...requiredRegularField(getDoctorsPackQuestions.other_medical_areas.required)
            }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('otherMedicalAreas')"
            :data-vv-as="$t('label.question')"
            data-vv-name="otherMedicalAreas"
            class="small"
            :disabled="readOnly"
          />
        </div>
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('members')"
        class="mt-4 order-2"
      >
        <p
          class="header mb-0"
          :class="{ required: getDoctorsPackQuestions.members.required }"
        >
          {{ getDoctorsPackQuestions.members?.label }}
        </p>
        <v-checkbox
          v-for="(member, index) in representatives"
          :key="index"
          v-model="members"
          v-validate="{ required: getDoctorsPackQuestions.members.required }"
          :disabled="readOnly"
          :label="member?.firstName"
          type="checkbox"
          off-icon=""
          on-icon="check"
          multiple
          :checked="
            members.some((e) => {
              return e.id === index
            })
          "
          :value="member.id"
          :error="errors.has('members')"
          :error-messages="index + 1 === representatives.length ? errors.collect('members') : null"
          data-vv-as=" "
          :data-vv-name="'members'"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('problem_description')"
        class="mt-4 order-3"
      >
        <p
          class="header mb-0"
          :class="{ required: getDoctorsPackQuestions.problem_description.required }"
        >
          {{ getDoctorsPackQuestions.problem_description?.label }}
        </p>
        <p
          v-for="(text, index) in getDoctorsPackQuestions.problem_description.text"
          :key="index"
          class="f-14 mb-0 mb-4 font-italic font-weight-regular"
        >
          {{ text }}
        </p>
        <v-textarea
          v-model="problemDescription"
          v-validate="{ ...regularLongText(getDoctorsPackQuestions.problem_description.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('problemDescription')"
          :data-vv-as="$t('label.question')"
          data-vv-name="problemDescription"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('problem_since')"
        class="mt-4 order-4"
      >
        <p
          class="header mb-0"
          :class="{ required: getDoctorsPackQuestions.problem_since.required }"
        >
          {{ getDoctorsPackQuestions.problem_since?.label }}
        </p>
        <v-textarea
          v-model="problemSince"
          v-validate="{ ...regularLongText(getDoctorsPackQuestions.problem_since.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('problemSince')"
          :data-vv-as="$t('label.question')"
          data-vv-name="problemSince"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('type_of_help')"
        class="mt-4"
        :class="hasResources ? 'order-6' : 'order-5'"
      >
        <p
          class="header"
          :class="{ required: getDoctorsPackQuestions.type_of_help.required }"
        >
          {{ getDoctorsPackQuestions.type_of_help?.label }}
        </p>
        <v-textarea
          v-model="typeOfHelp"
          v-validate="{ ...requiredRegularField(getDoctorsPackQuestions.type_of_help.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('typeOfHelp')"
          :data-vv-as="$t('label.question')"
          data-vv-name="typeOfHelp"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('receiving_medical_help')"
        class="mt-4"
        :class="hasResources ? 'order-5' : 'order-6'"
      >
        <p
          class="header"
          :class="{ required: getDoctorsPackQuestions.receiving_medical_help.required }"
        >
          {{ getDoctorsPackQuestions.receiving_medical_help?.label }}
        </p>
        <v-textarea
          v-if="
            getDoctorsPackQuestions.receiving_medical_help.hasOwnProperty('type') &&
              getDoctorsPackQuestions.receiving_medical_help.type === 'text_area'
          "
          v-model="receivingMedicalHelp"
          v-validate="{
            ...regularLongText(getDoctorsPackQuestions.receiving_medical_help.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('receivingMedicalHelp')"
          data-vv-as=" "
          data-vv-name="receivingMedicalHelp"
          auto-grow
          :disabled="readOnly"
        />
        <ConfirmButtons
          v-else
          v-model="receivingMedicalHelp"
          v-validate="{ required: getDoctorsPackQuestions.receiving_medical_help.required }"
          string
          :error-messages="errors.collect('receivingMedicalHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'receivingMedicalHelp'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          receivingMedicalHelp === 'no' &&
            getDoctorsPackQuestions.hasOwnProperty('receiving_medical_help_explain')
        "
        class="mt-4 order-7"
      >
        <p
          class="header custom lA"
          :class="{ required: getDoctorsPackQuestions.receiving_medical_help_explain.required }"
        >
          {{ getDoctorsPackQuestions.receiving_medical_help_explain?.label }}
        </p>
        <v-textarea
          v-model="receivingMedicalHelpExplain"
          v-validate="{
            ...requiredRegularField(getDoctorsPackQuestions.receiving_medical_help_explain.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('receivingMedicalHelpExplain')"
          :data-vv-as="$t('label.question')"
          data-vv-name="receivingMedicalHelpExplain"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('problem_fixing_attempt')"
        class="mt-4 order-7"
      >
        <p
          class="header"
          :class="{ required: getDoctorsPackQuestions.problem_fixing_attempt.required }"
        >
          {{ getDoctorsPackQuestions.problem_fixing_attempt?.label }}
        </p>
        <ConfirmButtons
          v-model="problemFixingAttempt"
          v-validate="{
            ...regularLongText(getDoctorsPackQuestions.problem_fixing_attempt.required)
          }"
          string
          :error-messages="errors.collect('problemFixingAttempt')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'problemFixingAttempt'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          problemFixingAttempt === 'yes' &&
            getDoctorsPackQuestions.hasOwnProperty('problem_fixing_attempt_description')
        "
        class="mt-4 order-7"
      >
        <p
          class="header custom lA"
          :class="{ required: getDoctorsPackQuestions.problem_fixing_attempt_description.required }"
        >
          {{ getDoctorsPackQuestions.problem_fixing_attempt_description?.label }}
        </p>
        <v-textarea
          v-model="problemFixingAttemptDescription"
          v-validate="{
            ...regularLongText(getDoctorsPackQuestions.problem_fixing_attempt_description.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('problemFixingAttemptDescription')"
          :data-vv-as="$t('label.question')"
          data-vv-name="problemFixingAttemptDescription"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getDoctorsPackQuestions.hasOwnProperty('medic_help')"
        class="mt-4 order-8"
      >
        <p
          class="header mb-1"
          :class="{ required: getDoctorsPackQuestions.medic_help.required }"
        >
          {{ getDoctorsPackQuestions.medic_help?.label }}
        </p>
        <ConfirmButtons
          v-model="medicHelp"
          v-validate="{ required: getDoctorsPackQuestions.medic_help.required }"
          string
          :error-messages="errors.collect('medicHelp')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'medicHelp'"
          :disabled="readOnly"
        />
        <template v-if="medicHelp === 'yes' && Boolean(getDoctorsPackQuestions?.medic_help?.text)">
          <p
            v-for="(text, index) in getDoctorsPackQuestions.medic_help.text"
            :key="index"
            class="f-14 mb-0 mt-1"
          >
            {{ text }}
          </p>
        </template>
      </li>
      <li
        v-if="showConfirmButtons"
        class="mt-4 order-8"
      >
        <p
          class="header"
          :class="{ required: getDoctorsPackQuestions.online_help_consent.required }"
        >
          {{ getDoctorsPackQuestions.online_help_consent?.label }}
        </p>
        <ConfirmButtons
          v-model="onlineHelpConsent"
          v-validate="{ required: getDoctorsPackQuestions.online_help_consent.required }"
          string
          :error-messages="errors.collect('onlineHelpConsent')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'onlineHelpConsent'"
          :disabled="readOnly"
        />
      </li>
    </ul>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';
import has from 'lodash.has';
const { mapFields } = createHelpers({
    getterType: 'sheet/getDoctorsPackField',
    mutationType: 'sheet/updateDoctorsPackField'
});

export default {
    name: 'DoctorsPack',
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            showOtherField: false,
            OTHER_MEDICAL_AREAS_2019: 69,
            OTHER_MEDICAL_AREAS_2020: 358
        };
    },
    computed: {
        ...mapFields([
            'checkedMedicalAreas',
            'receivingMedicalHelp',
            'receivingMedicalHelpExplain',
            'typeOfHelp',
            'otherMedicalAreas',
            'medicHelp',
            'members',
            'problemDescription',
            'problemSince',
            'problemFixingAttempt',
            'problemFixingAttemptDescription',
            'onlineHelpConsent'
        ]),
        ...mapGetters('sheet', ['getDoctorsPackQuestions']),
        showConfirmButtons () {
            return this.medicHelp === 'yes' && has(this.getDoctorsPackQuestions, 'online_help_consent');
        },
        medicalAreas () {
            return this.getResources(this.getDoctorsPackQuestions.checked_medical_areas);
        },
        hasResources () {
            return has(this.getDoctorsPackQuestions?.checked_medical_areas, 'resources');
        },
        representatives () {
            return this.$store.getters['sheet/getRepresentatives'];
        }
    },
    components: {
        ConfirmButtons
    },
    mixins: [sheetValidationRules, sheetResourcesGetter],
    watch: {
        checkedMedicalAreas: {
            handler () {
                this.showOtherField =
          (this.hasResources && this.checkedMedicalAreas.includes(this.OTHER_MEDICAL_AREAS_2019)) ||
          this.checkedMedicalAreas.includes(this.OTHER_MEDICAL_AREAS_2020);
                if (this.showOtherField === false) {
                    this.otherMedicalAreas = null;
                }
            },
            immediate: true
        },
        receivingMedicalHelp (val) {
            if (val === 'yes') {
                this.receivingMedicalHelpExplain = null;
            }
        },
        medicHelp: {
            handler (val) {
                if (val !== 'yes') {
                    this.onlineHelpConsent = null;
                }
            },
            immediate: true
        },
        problemFixingAttempt (problemFixingAttempt) {
            if (problemFixingAttempt !== 'yes') {
                this.problemFixingAttemptDescription = null;
            }
        },
        representatives (representatives, oldRepresentatives) {
            if (representatives.length > 0 && representatives.length !== oldRepresentatives.length) {
                this.members = this.members.filter((member) =>
                    representatives.some((rep) => rep.id === member)
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.questions-list {
  display: flex;
  flex-direction: column;
}
.order {
  &-1 {
    order: 1;
  }
  &-2 {
    order: 2;
  }
  &-3 {
    order: 3;
  }
  &-4 {
    order: 4;
  }
  &-5 {
    order: 5;
  }
  &-6 {
    order: 6;
  }
  &-7 {
    order: 7;
  }
  &-8 {
    order: 8;
  }
  &-9 {
    order: 9;
  }
}
</style>
